<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <div class="setup-ct">
      <rest-form
        v-if="model"
        ref="basic-setup-form"
        :model="model"
        :save-endpoint="saveEndpoint"
        :rules="rules"
        :disabled="isReadOnly"
        :after-save="afterSave"
        @on-error="isLoading = false"
      >
        <form-section
          :title="$t('Gamification_GameBasicSetupForm_CampaignName')"
          :description="
            $t('Gamification_GameBasicSetupForm_CampaignNameDescription')
          "
          :is-required="true"
        >
          <rest-form-item
            prop="name"
            :label="$t('Gamification_GameBasicSetupForm_Name')"
            class="default-width"
          >
            <el-input v-model="model.name" required />
          </rest-form-item>
        </form-section>
        <form-section
          :title="$t('Gamification_GameBasicSetupForm_StartAndEndAt')"
          :description="
            $t('Gamification_GameBasicSetupForm_StartAndEndAtDescription')
          "
          :is-required="true"
        >
          <rest-form-item
            prop="ends_after_last_round"
            class="default-width"
            required
            :label="$t('Gamification_GameBasicSetupForm_EndCampaignLabel')"
          >
            <el-switch
              v-model="model.ends_after_last_round"
              active-color="#1d87c9"
              :active-value="true"
              :inactive-value="false"
              :disabled="!isCreationMode"
              @change="onEndsAfterLastRoundChange"
            />
          </rest-form-item>
          <div class="default-width date-time-ct">
            <rest-form-item
              prop="starts_at"
              :label="$t('Gamification_GameBasicSetupForm_StartAtLabel')"
              class="flex-1"
            >
              <el-date-picker
                v-model="model.starts_at"
                type="datetime"
                format="MMMM d yyyy HH:mm"
                :picker-options="startPickerOptions"
                default-time="00:00:00"
                required
                :disabled="!isCreationMode"
                @change="onDateChange($event, 'starts_at', 'ends_at')"
              />
            </rest-form-item>
            <rest-form-item
              v-show="!model.ends_after_last_round"
              prop="ends_at"
              :label="$t('Gamification_GameBasicSetupForm_EndAtLabel')"
              class="flex-1"
            >
              <el-date-picker
                v-model="model.ends_at"
                type="datetime"
                format="MMMM d yyyy HH:mm"
                :picker-options="endPickerOptions"
                default-time="23:59:59"
                :required="!model.ends_after_last_round"
                :disabled="!isCreationMode"
                @change="onDateChange($event, 'ends_at', 'starts_at')"
              />
            </rest-form-item>
            <rest-form-item
              v-show="model.ends_after_last_round"
              prop="ends_after_last_round_days"
              :label="$t('Gamification_GameBasicSetupForm_EndDaysLabel')"
              class="flex-1"
            >
              <el-input
                v-model.number="model.ends_after_last_round_days"
                :required="model.ends_after_last_round"
                :disabled="!isCreationMode"
                type="number"
                min="1"
              />
            </rest-form-item>
          </div>
        </form-section>
        <form-section
          :title="$t('Gamification_GameBasicSetupForm_TimeZone')"
          :description="
            $t('Gamification_GameBasicSetupForm_TimeZoneDescription')
          "
          :is-required="true"
        >
          <rest-form-item
            prop="timezone"
            :label="$t('Gamification_GameBasicSetupForm_TimeZone')"
            class="default-width"
            required
          >
            <el-select
              v-model="model.timezone"
              filterable
              clearable
              :disabled="!isCreationMode"
            >
              <el-option
                v-for="(label, value) in timezoneOptions"
                :key="value"
                :value="value"
                :label="label"
              >
                <span v-tooltip>{{ label }}</span>
              </el-option>
            </el-select>
          </rest-form-item>
        </form-section>
        <form-section
          :title="$t('Gamification_GameBasicSetupForm_DefaultSegment')"
          :description="
            $t('Gamification_GameBasicSetupForm_DefaultSegmentDescription')
          "
        >
          <segment-select
            prop="default_segment_id"
            v-model="model.default_segment_id"
            :label="$t('Gamification_GameBasicSetupForm_Segment')"
            clearable
            class="default-width"
            @change="revalidateField('excludes_segment_id')"
          />
        </form-section>
        <form-section
          :title="$t('Gamification_GameBasicSetupForm_ExcludeSegment')"
          :description="
            $t('Gamification_GameBasicSetupForm_ExcludeSegmentDescription')
          "
        >
          <segment-select
            prop="excludes_segment_id"
            v-model="model.excludes_segment_id"
            :label="$t('Gamification_GameBasicSetupForm_Segment')"
            clearable
            class="default-width"
            @change="revalidateField('default_segment_id')"
          />
        </form-section>
      </rest-form>
    </div>

    <div v-if="isCreationMode" class="footer">
      <div class="footer-ct">
        <el-button
          type="primary"
          :disabled="isSubmitDisabled"
          @click="saveBasicSetup"
          data-qa="next-step-btn"
        >
          {{ submitButtonLabel }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
      </div>
    </div>
  </rest-page>
</template>
<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import _ from 'lodash'
import RestForm from '@/common/components/rest/RestForm'
import RestFormItem from '@/common/components/rest/RestFormItem'
import FormSection from '@/common/components/form/FormSection'
import SegmentSelect from '@/common/components/segment/SegmentSelect.vue'
import {
  checkIsButtonDisabled,
  castDate,
  getLatestDateFromRounds,
  getEarliestDateFromRounds
} from '@/modules/gamification/helpers'
import { GameCreationSubSteps } from '@/modules/gamification/enum/GameCreationStepsEnum.js'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage.vue'
import { EventBus } from '@/core/EventBus'
import { GameCampaignType } from '@/modules/gamification/enum/GameCampaignTypeEnum'

export default {
  name: 'GameBasicSetupForm',
  components: {
    RestPage,
    RestForm,
    RestFormItem,
    FormSection,
    SegmentSelect
  },
  data() {
    return {
      isLoading: false,
      step: GameCreationSubSteps.BASIC_SET_UP,
      model: {
        name: undefined,
        starts_at: undefined,
        ends_at: undefined,
        timezone: undefined,
        default_segment_id: undefined,
        excludes_segment_id: undefined,
        game_type: GameCampaignType[this.game.game_campaign_type],
        ends_after_last_round: false,
        ends_after_last_round_days: undefined
      },
      initialStateModel: undefined,
      shouldGoToNextStep: false,
      previousEndsAt: undefined,
      previousEndsAfterLastRoundDays: undefined,
      bracket: undefined
    }
  },
  props: {
    isBracketPredictorGame: {
      type: Boolean,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isCreationMode: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    },
    gameCampaignId: {
      type: Number
    },
    game: {
      type: Object,
      required: true
    },
    hasSavedBracket: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['timezones']),
    timezoneOptions() {
      return Object.fromEntries(
        Object.entries(this.timezones).filter(([key]) => key !== '')
      )
    },
    requiredFields() {
      if (this.model.ends_after_last_round) {
        return ['name', 'starts_at', 'ends_after_last_round_days', 'timezone']
      }

      return ['name', 'starts_at', 'ends_at', 'timezone']
    },
    isSubmitDisabled() {
      return (
        checkIsButtonDisabled(this.requiredFields, this.model) ||
        this.isLoading ||
        this.isReadOnly
      )
    },
    hasChanges() {
      return !_.isEqual(this.model, this.initialStateModel)
    },
    rules() {
      const defaultRules = {
        name: {
          required: true,
          message: this.$t(
            'Gamification_GameBasicSetupForm_CampaignNameRequired'
          ),
          trigger: 'blur'
        },
        starts_at: [
          {
            required: true,
            message: this.$t('Gamification_GameBasicSetupForm_StartAtRequired'),
            trigger: ['blur', 'change']
          },
          {
            validator: (_rule, value, callback) => {
              const endsAt = this.model.ends_at
              if (!value || !endsAt) {
                return callback()
              }

              if (moment(value).isSameOrAfter(moment(endsAt))) {
                return callback(
                  new Error(this.$t('Gamification_StartTimeValidation'))
                )
              }

              const firstGameDay = this.getManualBracketFirstGameDay

              if (firstGameDay && moment(value).isAfter(firstGameDay, 'day')) {
                return callback(
                  new Error(this.$t('Gamification_StartTimeValidation_GameDay'))
                )
              }

              callback()
            },
            trigger: ['blur', 'change']
          }
        ],
        timezone: {
          required: true,
          message: this.$t('Gamification_GameBasicSetupForm_TimezoneRequired'),
          trigger: 'change'
        },
        default_segment_id: [
          {
            validator: this.makeSegmentValidator('excludes_segment_id'),
            trigger: 'change'
          }
        ],
        excludes_segment_id: [
          {
            validator: this.makeSegmentValidator('default_segment_id'),
            trigger: 'change'
          }
        ]
      }

      if (this.model.ends_after_last_round) {
        defaultRules.ends_after_last_round_days = [
          {
            required: true,
            message: this.$t('Gamification_GameBasicSetupForm_EndDaysRequired'),
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              if (value !== '') {
                if (value < 1) {
                  return callback(
                    new Error(
                      this.$t(
                        'Gamification_GameBasicSetupForm_EndDaysMustBePositive'
                      )
                    )
                  )
                }

                if (!Number.isInteger(value)) {
                  return callback(
                    new Error(
                      this.$t(
                        'Gamification_GameBasicSetupForm_EndDaysMustBeInteger'
                      )
                    )
                  )
                }
              }

              callback()
            },
            trigger: ['blur', 'change']
          }
        ]
      } else {
        defaultRules.ends_at = [
          {
            required: true,
            message: this.$t('Gamification_GameBasicSetupForm_EndAtRequired'),
            trigger: ['blur', 'change']
          },
          {
            validator: (_rule, value, callback) => {
              const startsAt = this.model.starts_at

              if (!value || !startsAt) return callback()

              if (moment(value).isSameOrBefore(moment(startsAt))) {
                return callback(
                  new Error(this.$t('Gamification_EndTimeValidation'))
                )
              }

              const latestGameDay = this.getManualBracketLatestGameDay

              if (
                latestGameDay &&
                moment(value).isBefore(latestGameDay, 'day')
              ) {
                return callback(
                  new Error(this.$t('Gamification_EndTimeValidation_GameDay'))
                )
              }

              callback()
            },
            trigger: ['blur', 'change']
          }
        ]
      }

      return defaultRules
    },
    submitButtonLabel() {
      if (this.isBracketPredictorGame) {
        return this.$t('Gamification_GameBasicSetupForm_SaveButton')
      }

      return this.$t('Gamification_GameCompetitionTeamsForm_SaveButton')
    },
    startPickerOptions() {
      return {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => {
          const now = moment().startOf('day')
          const current = moment(date).startOf('day')

          if (current.isBefore(now)) return true

          if (this.model.ends_at) {
            return current.isAfter(moment(this.model.ends_at).startOf('day'))
          }

          return false
        }
      }
    },

    endPickerOptions() {
      return {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => {
          const now = moment().startOf('day')
          const current = moment(date).startOf('day')

          if (current.isBefore(now, 'day')) return true

          if (this.model.starts_at) {
            return current.isBefore(moment(this.model.starts_at).startOf('day'))
          }

          return false
        }
      }
    },

    saveEndpoint() {
      if (this.model.id) {
        return projectEndpoint({
          method: 'put',
          url: `gamify/${this.$route.params.id}/update-setup-campaign/${this.gameCampaignId}`
        })
      }
      this.model.create_default_round = true
      return projectEndpoint({
        method: 'post',
        url: `gamify/${this.$route.params.id}/setup-create-campaign`
      })
    },

    fetchEndpoint() {
      const endpoints = [this.fetchCampaignEndpoint]

      if (this.hasSavedBracket) {
        endpoints.push(this.fetchBracketStructure)
      }

      return endpoints
    },

    fetchCampaignEndpoint() {
      if (!this.gameCampaignId) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-game-campaign/${this.gameCampaignId}`,
        handler: data => {
          this.model = data['data']

          this.model.starts_at = castDate(this.model.starts_at)
          this.model.ends_at = castDate(this.model.ends_at)
          if (this.model.default_segment_id) {
            this.model.default_segment_id = Number(
              this.model.default_segment_id
            )
          }
          if (this.model.excludes_segment_id) {
            this.model.excludes_segment_id = Number(
              this.model.excludes_segment_id
            )
          }

          this.initialStateModel = _.cloneDeep(this.model)
        }
      })
    },

    fetchBracketStructure() {
      if (!this.gameCampaignId) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-manual-bracket/${this.gameCampaignId}`,
        handler: data => {
          if (data.data) {
            this.bracket = data.data
          }
        }
      })
    },

    getManualBracketFirstGameDay() {
      if (
        !this.bracket ||
        !this.bracket.bracket ||
        !this.bracket.bracket.length
      ) {
        return null
      }

      const rounds = this.bracket.bracket

      return getEarliestDateFromRounds([rounds[0]])
    },

    getManualBracketLatestGameDay() {
      if (
        !this.bracket ||
        !this.bracket.bracket ||
        !this.bracket.bracket.length
      ) {
        return null
      }

      const rounds = this.bracket.bracket

      return getLatestDateFromRounds([rounds[rounds.length - 1]])
    }
  },
  mounted() {
    this.initialStateModel = _.cloneDeep(this.model)
  },
  watch: {
    isSubmitDisabled: {
      handler(newVal) {
        this.$emit('check', newVal)
      },
      immediate: true
    },
    hasChanges: {
      handler(newVal) {
        this.$emit('checkChanges', newVal)
      },
      immediate: true
    },
    saveFlag: {
      async handler() {
        await this.saveBasicSetup(false)
      }
    }
  },
  methods: {
    async saveBasicSetup(shouldGoToNextStep = true) {
      this.isLoading = true

      try {
        const valid = await this.$refs['basic-setup-form'].validate()

        if (valid) {
          this.$refs['basic-setup-form'].save()
          this.shouldGoToNextStep = shouldGoToNextStep
        }
      } catch (e) {
        console.error(e)
        this.isLoading = false
      }
    },
    afterSave(data, finishLoading) {
      finishLoading()
      this.isLoading = false
      if (!this.model.id && data.data.id) {
        EventBus.$emit('saveGameCampaignId', data.data.id)
      }

      if (data.data) {
        EventBus.$emit('updateCampaign', {
          ends_at: data.data.ends_at,
          starts_at: data.data.starts_at
        })
      }

      this.initialStateModel = _.cloneDeep(this.model)

      if (this.shouldGoToNextStep) {
        this.$emit('goToNextStep', this.step)
      } else if (this.isCreationMode) {
        EventBus.$emit('updateStep', this.step)
        this.$nextTick(() => {
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        })
      }
    },
    onEndsAfterLastRoundChange(newVal) {
      if (newVal) {
        this.model.ends_after_last_round_days =
          this.previousEndsAfterLastRoundDays || 90

        this.previousEndsAt = this.model.ends_at
        this.model.ends_at = undefined
      } else {
        this.model.ends_at = this.previousEndsAt

        this.previousEndsAfterLastRoundDays = this.model.ends_after_last_round_days
        this.model.ends_after_last_round_days = undefined
      }
      this.revalidateField('starts_at')
    },
    makeSegmentValidator(otherFieldName) {
      return (_rule, value, callback) => {
        const otherValue = this.model[otherFieldName]

        if (value && value !== '' && value === otherValue) {
          return callback(
            new Error(this.$t('Gamification_GameBasicSetupForm_SegmentsMatch'))
          )
        }

        callback()
      }
    },
    revalidateField(field) {
      if (!this.model || !this.model[field]) return

      this.$refs['basic-setup-form'].validateField(field)
    },
    onDateChange(payload, modelField, revalidateField) {
      this.model[modelField] = castDate(payload)
      this.revalidateField(revalidateField)
    }
  }
}
</script>
<style scoped>
.setup-ct {
  padding-bottom: 60px;
}
.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}
.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
.date-time-ct {
  display: flex;
  gap: 24px;
  align-items: center;
}
.default-width {
  width: 596px;
}
.flex-1 {
  flex: 1;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/setup/common/GameBasicSetupForm.vue